<template>
  <PageTemplate>
    <div class="d-flex justify-content-between">
      <h6 class="element-header">
        {{ $t('general.contacts') }}
      </h6>

      <CreateButton
          label="Privatkunde hnzufügen"
          :to="{ name: 'addressBookPrivateCustomerForm' }"/>
    </div>

    <div class="element-box">
      <h5 class="form-header mb-4">
        Privatkunden
      </h5>

      <Search
          :filters="filters"
          @search="getPrivateCustomers"/>

      <PerPage
          :values="[10, 25, 50, 100]"
          @update:perPage="getPrivateCustomers"/>

      <ExcelExportButton
          style="margin-right: 15px;"
          :label="$t('general.excel_export')"
          :loading="isExcelLoaded"
          @click="getExcelExport"/>

      <EmptyState
          v-if="!privateCustomers?.data?.length"
          icon="fa-address-book"
          title="Keine Kontakte gefunden"
          subtitle="Es wurden keine Kontakte gefunden, die Ihren Suchkriterien entsprechen."/>

      <div v-else class="table-responsive overflow-auto pt-4">
        <table class="table table-striped">
          <thead>
          <tr>
            <th>
              <a href="#"  @click="sortBy('firstName')">
                <span>{{ $t('general.first_name') }}</span>
                <i :class="getIconClass('firstName')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('lastName')">
                <span>{{ $t('general.last_name') }}</span>
                <i :class="getIconClass('lastName')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('company')">
                <span>{{ $t('general.company_client') }}</span>
                <i :class="getIconClass('company')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('phoneNumber')">
                {{ $t('general.phone_number') }}
                <i :class="getIconClass('phoneNumber')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('email')">
                {{ $t('general.email') }}
                <i :class="getIconClass('email')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('address')">
                {{ $t('general.street') }}
                <i :class="getIconClass('address')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('zip')">
                {{ $t('general.postal_code') }}
                <i :class="getIconClass('zip')"></i>
              </a>
            </th>
            <th>
              <a href="#" @click="sortBy('city')">
                {{ $t('general.city') }}
                <i :class="getIconClass('city')"></i>
              </a>
            </th>
            <th>
              <a href="#"  @click="sortBy('position')">
                {{ $t('general.position') }}
                <i :class="getIconClass('position')"></i>
              </a>
            </th>
            <th>
              {{ $t('general.info') }}
            </th>
            <th style="min-width: 130px!important; text-align: center">
              {{ $t('general.actions') }}
            </th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(customer) in privateCustomers?.data" :key="customer.id">
            <td>{{ customer?.firstName }}</td>
            <td>{{ customer?.lastName }}</td>
            <td>{{ customer?.company }}</td>
            <td>{{ customer?.phoneNumber }}</td>
            <td>{{ customer?.email }}</td>
            <td>{{ customer?.address }}</td>
            <td>{{ customer?.zip }}</td>
            <td>{{ customer?.city }}</td>
            <td>{{ customer?.position }}</td>
            <td>{{ customer?.info ? 'JA' : 'NEIN' }}</td>
            <td class="text-end">
              <button class="btn btn-primary"
                      @click="router.push({name: 'addressBookPrivateCustomerForm', params: {id: customer?.id} })">
                <i class="os-icon os-icon-edit"></i>
              </button>
              <button class="btn btn-danger ms-2"
                      @click="deleteModal(customer?.id!)">
                <i class="os-icon os-icon-trash"></i>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <Pagination
          v-if="privateCustomers?.pagination?.total_pages ?? 0 > 1"
          :totalPages="privateCustomers?.pagination?.total_pages"
          :currentPage="privateCustomers?.pagination?.current_page"
          :totalItems="privateCustomers?.pagination?.total_items"
          :perPage="privateCustomers?.pagination?.items_per_page"
          @pagechanged="getPrivateCustomers"/>
    </div>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDelete">
    </ModalDelete>

  </PageTemplate>
</template>

<script setup lang="ts">
import privateCustomerService from '@/api/AddressBook/privateCustomer';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { downloadFile, getSortIconClass } from '@/shared/functions';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Search from '../Search.vue';
import {IAddressBookFilters, IAddressBookList} from "@/interfaces/AddressBook/IAddressBook";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const { t }             = useI18n();
const isLoaded          = ref(false);
const isSearching       = ref(false);
const showModal         = ref(false);
const isExcelLoaded = ref(false);

const privateCustomerId = ref<number | null>(null);
const privateCustomers  = ref<IAddressBookList>();
const filters           = ref<IAddressBookFilters>({
  firstName: '',
  lastName: '',
  city: '',
  company: '',
  phoneNumber: '',
  position: '',
  email: '',
  federalState: '',
  orderBy: 'DESC',
  sortBy: '',
  page: 1,
  limit: 10,
});

async function getPrivateCustomers(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IAddressBookFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = privateCustomers.value?.pagination?.items_per_page;

  try {
    const { data } = await privateCustomerService.getPrivateCustomers({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    privateCustomers.value = data;
    isSearching.value      = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

async function confirmDelete() {
  try {
    await privateCustomerService.deletePrivateCustomer(privateCustomerId.value!);
    await getPrivateCustomers();
    showModal.value = false;

    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

function deleteModal(id: number) {
  CloseToast();
  privateCustomerId.value = id;
  showModal.value = true;
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getPrivateCustomers();
}

async function getExcelExport() {
  await downloadFile('/address-book/private-customer/excel-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  CloseToast()
  await getPrivateCustomers();

  isLoaded.value = true;
});

</script>

<style scoped>

</style>